/**
 * AI Assistant Service
 * Chat, history, status, Report Issue, the AI Settings config and the usage / session / correction
 * review pages, all on the real API (no mock data).
 */

import ApiService from '../api.service';
import { can } from '../../utils/roleUtils';

const unwrap = (res) => (res && res.data ? res.data.data : null);

// ── Suggested Questions ──────────────────────────────────────────

// SPEC §5: each question is offered only when the user can use the tool that answers it.
const SUGGESTED_QUESTIONS = [
  { text: 'Show overdue work orders', feature: 'work_order' },
  { text: "Show today's work orders", feature: 'work_order' },
  { text: 'What happened today?', feature: 'work_order' },
  { text: 'How many unpaid invoices do we have?', feature: 'invoice' },
  { text: 'Which organizations have overdue balances?', feature: 'invoice' },
  { text: 'Which documents expire this week?', feature: 'thaklis_features' },
  { text: "What's our cash position?", feature: 'finance', category: 'accounting' },
  { text: 'What VAT do we owe?', feature: 'finance', category: 'accounting' },
  { text: 'Show team workload', feature: 'settings' },
  { text: 'What documents does a visa renewal need?', feature: 'service_catalog' },
];
const GENERAL_QUESTION = 'How long does an EID renewal take?';
const MAX_SUGGESTIONS = 5;

// ── Export ────────────────────────────────────────────────────────

const aiAssistantService = {
  getStatus: async () => unwrap(await ApiService.get('/api/ai/status')),

  // Omit sessionId to start a new session; the response carries the new session_id
  chat: async (sessionId, message) =>
    unwrap(await ApiService.post('/api/ai/chat', sessionId ? { session_id: sessionId, message } : { message })),

  // A session with no exchanges yet returns 404, which simply means an empty chat
  getHistory: async (sessionId) => {
    try {
      return unwrap(await ApiService.get(`/api/ai/history/${sessionId}`));
    } catch (err) {
      if (err?.response?.status === 404) return { session_id: sessionId, exchanges: [] };
      throw err;
    }
  },

  reportCorrection: async (exchangeId, humanFeedback) =>
    unwrap(await ApiService.post('/api/ai/corrections', { exchange_id: exchangeId, human_feedback: humanFeedback })),

  // Up to 4 permitted data questions plus one general question that needs no data tool
  getSuggestedQuestions: (user, availableCategories = []) => {
    const permitted = SUGGESTED_QUESTIONS
      .filter(q => can(user, q.feature, 'read') && (!q.category || availableCategories.includes(q.category)))
      .map(q => q.text);
    return [...permitted.slice(0, MAX_SUGGESTIONS - 1), GENERAL_QUESTION];
  },

  getConfig: async () => unwrap(await ApiService.get('/api/ai/config')),

  updateConfig: async (config) => unwrap(await ApiService.put('/api/ai/config', config)),

  getUsage: async (days = 30) => unwrap(await ApiService.get('/api/ai/usage', { params: { days } })),

  getDailyUsage: async (days = 30) => unwrap(await ApiService.get('/api/ai/usage/daily', { params: { days } })),

  // Omits empty filters so the server only applies the ones chosen
  getSessions: async ({ userId, from, to, page = 0, size = 20 } = {}) => {
    const params = { page, size };
    if (userId) params.user_id = userId;
    if (from) params.from = from;
    if (to) params.to = to;
    return unwrap(await ApiService.get('/api/ai/sessions', { params }));
  },

  getSessionDetail: async (sessionId) => unwrap(await ApiService.get(`/api/ai/sessions/${sessionId}`)),

  getCorrections: async ({ status, page = 0, size = 20 } = {}) => {
    const params = { page, size };
    if (status) params.status = status;
    return unwrap(await ApiService.get('/api/ai/corrections', { params }));
  },

  markCorrectionReviewed: async (id) => unwrap(await ApiService.put(`/api/ai/corrections/${id}/review`)),

  // Platform figures across all typing centers (PLATFORM_ADMIN)
  getPlatformUsage: async ({ days = 30, page = 0, size = 20 } = {}) =>
    unwrap(await ApiService.get('/api/ai/admin/usage', { params: { days, page, size } })),

  getPlatformHealth: async ({ days = 30, page = 0, size = 20 } = {}) =>
    unwrap(await ApiService.get('/api/ai/admin/health', { params: { days, page, size } })),
};

export default aiAssistantService;
